import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { IoRefreshOutline } from "react-icons/io5";
import styled from "../../../component/commonCard/style.module.css";
import KpiCard from "../../../component/commonCard/parts/KpiCard";
import NotAvailableYet from "../NotAvailableYet";
import { TEAL, GREEN, ALERT_ERROR } from "../../../component/commonCard/parts/theme";
import { getRealtimeAnalytics } from "../../../api/HttpApis";
import { showToast } from "../../../utils/Toastify/Toastify";
import { formatDate } from "../../../utils/dateFormatter";

import ExportButtons from "../ExportButtons";

const POLL_MS = 15000;

// No websocket/push infra exists in the backend today, so this is honestly
// polling-based (auto-refresh every 15s), not a fake "● Live" badge over a
// static payload the way the previous version of this tab was.
const RealtimeSection = () => {
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform") || "";
  const contentType = searchParams.get("contentType") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Separate from `loading` (which only covers the very first load and blanks
  // the whole section) -- this drives the "Refresh now" button's own spinner
  // for every fetch after that: manual clicks, the 15s poll, and the
  // visibility-change refetch, so it's clear a refresh is in flight instead
  // of the button looking inert while data silently updates underneath it.
  const [refreshing, setRefreshing] = useState(false);

  // fetchData is invoked from several places that can overlap (poll interval,
  // visibility-change refetch, manual "Refresh now", and the effect below
  // reacting to platform/contentType) -- a plain `cancelled` flag from a
  // single effect's cleanup wouldn't cover all of them. A monotonically
  // increasing request id lets every call discard its result if a newer
  // call has since been issued, so a slow response for a since-changed
  // platform can never overwrite the current view.
  const requestIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setRefreshing(true);
    try {
      const res = await getRealtimeAnalytics({ windowMinutes: 15, platform, contentType });
      if (requestId === requestIdRef.current) setData(res?.data?.data || null);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        showToast(err?.response?.data?.message || "Failed to load realtime analytics", "error");
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [platform, contentType]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    // Skip polling while the tab is hidden -- this endpoint is uncached and
    // hits the biggest table in the DB, so a tab left open overnight
    // shouldn't keep polling it every 15s for no one to see. Refetch
    // immediately on return so the view isn't stale when it's looked at again.
    const interval = setInterval(() => {
      if (!document.hidden) fetchData();
    }, POLL_MS);
    const handleVisibilityChange = () => {
      if (!document.hidden) fetchData();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchData]);

  if (loading) return <div className={styled.loading}>Loading realtime analytics…</div>;

  const isAllPlatforms = Boolean(data?._meta);

  return (
    <>
      <div className={styled.chartCardHead} style={{ marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <span className={styled.realtimeStatus}>Auto-refreshing every 15s · last {data?.window_minutes || 15} min</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={fetchData}
            disabled={refreshing}
            className={styled.refreshButton}
          >
            <IoRefreshOutline className={refreshing ? styled.spinIcon : undefined} />
            {refreshing ? "Refreshing…" : "Refresh now"}
          </button>
          <ExportButtons
            rows={(data?.latest_events || []).map((e) => ({ time: e.timestamp, event_type: e.event_type, event_category: e.event_category, platform: e.platform || "" }))}
            filename="realtime-events"
            className={styled.inlineExportGroup}
          />
        </div>
      </div>

      <div className={styled.kpiRow}>
        <div title="PLAYBACK_ERROR events in the last window.">
          <KpiCard label="Playback Errors" value={(data?.playback_errors ?? 0).toLocaleString()} accent={ALERT_ERROR} />
        </div>
        <div title="Playback errors ÷ all playback events in the last window.">
          <KpiCard label="Error Rate" value={`${data?.error_rate_percent ?? 0}%`} accent={ALERT_ERROR} />
        </div>
        <div title={`Streaming sessions on live content with status=active right now.${data?._meta?.note ? ` ${data._meta.note}` : ""}`}>
          <KpiCard label={isAllPlatforms ? "Currently Live Viewers (all platforms)" : "Currently Live Viewers"} value={(data?.currently_live_viewers ?? 0).toLocaleString()} accent={GREEN} />
        </div>
        <div title={`Published live channels in the catalog.${data?._meta?.note ? ` ${data._meta.note}` : ""}`}>
          <KpiCard label={isAllPlatforms ? "Active Live Channels (all platforms)" : "Active Live Channels"} value={(data?.live_channels_active ?? 0).toLocaleString()} accent={TEAL} />
        </div>
      </div>

      <div className={styled.chartCard}>
        <div className={styled.chartCardHead}>
          <h3 className={styled.chartTitle}>Live Event Stream</h3>
        </div>
        {data?.latest_events?.length > 0 ? (
          <div className={styled.lbWrap}>
            <table className={styled.lbTable}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Category</th>
                  <th>Platform</th>
                </tr>
              </thead>
              <tbody>
                {data.latest_events.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate ? formatDate(e.timestamp) : new Date(e.timestamp).toLocaleString()}</td>
                    <td>{e.event_type}</td>
                    <td>{e.event_category}</td>
                    <td>{e.platform || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={styled.emptyHint}>No events in the last {data?.window_minutes || 15} minutes.</p>
        )}
      </div>

      <NotAvailableYet
        title="True Real-Time Push"
        reason="Live push updates aren't available yet, so this tab automatically refreshes every 15 seconds instead."
      />
    </>
  );
};

export default RealtimeSection;
